/**
 * Customer channel settings — WhatsApp + Instagram + Facebook Messenger +
 * the Gmail customer inbox. Served by the fasto_bi service (/svc/crm).
 */
import { useEffect, useState } from 'react';
import { Facebook, Instagram, Mail, MessageCircle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import {
  useMetaSettings,
  usePatchMetaSettings,
  type CrmChannelAccount,
  type CrmGmailChannel,
  type CrmMetaSettings,
} from '@/api/bi/crmSettings';
import { notify } from '@/lib/toast';

type ChannelKey = 'whatsapp_customer' | 'instagram' | 'facebook_messenger';

function emptyChannel(channel: string): CrmChannelAccount {
  return {
    channel,
    is_enabled: false,
    display_name: '',
    meta_app_id: '',
    meta_page_id: '',
    meta_ig_user_id: '',
    cloud_phone_number_id: '',
    cloud_waba_id: '',
    wa_template_fallback: '',
    wa_template_language: 'en_US',
    meta_verify_token: '',
    has_meta_app_secret: false,
    has_meta_access_token: false,
    last_sync_at: null,
    last_error: '',
    updated_at: null,
  };
}

export function CrmMetaSettingsPage() {
  const q = useMetaSettings();
  const patch = usePatchMetaSettings();
  const [form, setForm] = useState<CrmMetaSettings | null>(null);
  const [secrets, setSecrets] = useState<Record<ChannelKey, { secret: string; token: string }>>({
    whatsapp_customer: { secret: '', token: '' },
    instagram: { secret: '', token: '' },
    facebook_messenger: { secret: '', token: '' },
  });
  const [gmailPassword, setGmailPassword] = useState('');

  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);

  if (q.isLoading || !form) return <Skeleton className="h-96 w-full" />;
  if (q.isError) return <ErrorState title="Failed to load Meta settings" onRetry={() => q.refetch()} />;

  async function handleSave() {
    const payload: Partial<CrmMetaSettings> = {};
    (['whatsapp_customer', 'instagram', 'facebook_messenger'] as ChannelKey[]).forEach((key) => {
      const row = { ...form![key] };
      const s = secrets[key];
      const body: Record<string, unknown> = { ...row };
      if (s.secret) body.meta_app_secret = s.secret;
      if (s.token) body.meta_access_token = s.token;
      delete body.has_meta_app_secret;
      delete body.has_meta_access_token;
      delete body.last_sync_at;
      delete body.last_error;
      delete body.updated_at;
      payload[key] = body as unknown as CrmChannelAccount;
    });
    if (form!.gmail) {
      const body: Record<string, unknown> = { ...form!.gmail };
      if (gmailPassword) body.password = gmailPassword;
      delete body.has_password;
      delete body.last_poll_at;
      delete body.last_poll_status;
      delete body.last_poll_error;
      delete body.last_error;
      delete body.updated_at;
      payload.gmail = body as unknown as CrmGmailChannel;
    }
    try {
      await patch.mutateAsync(payload);
      notify.success('Customer channel settings saved');
      setSecrets({
        whatsapp_customer: { secret: '', token: '' },
        instagram: { secret: '', token: '' },
        facebook_messenger: { secret: '', token: '' },
      });
      setGmailPassword('');
    } catch {
      notify.error('Could not save channel settings');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="Customer channels"
        description="Customer WhatsApp (separate from staff alerts), Instagram, Facebook Messenger, and the Gmail inbox. Meta webhook: https://bi.fasto.com.np/svc/crm/webhooks/meta"
        actions={
          <Button onClick={() => void handleSave()} disabled={patch.isPending}>
            Save
          </Button>
        }
      />
      <div className="grid gap-4">
        <ChannelCard
          title="WhatsApp (customer inbox)"
          icon={MessageCircle}
          hint="Use a different phone_number_id than staff alerts on CRM Settings → WhatsApp."
          row={form.whatsapp_customer ?? emptyChannel('whatsapp_customer')}
          secrets={secrets.whatsapp_customer}
          onChange={(row) => setForm((f) => ({ ...f!, whatsapp_customer: row }))}
          onSecrets={(s) =>
            setSecrets((prev) => ({ ...prev, whatsapp_customer: { ...prev.whatsapp_customer, ...s } }))
          }
          showWa
        />
        <ChannelCard
          title="Instagram"
          icon={Instagram}
          row={form.instagram ?? emptyChannel('instagram')}
          secrets={secrets.instagram}
          onChange={(row) => setForm((f) => ({ ...f!, instagram: row }))}
          onSecrets={(s) => setSecrets((prev) => ({ ...prev, instagram: { ...prev.instagram, ...s } }))}
        />
        <ChannelCard
          title="Facebook Messenger"
          icon={Facebook}
          row={form.facebook_messenger ?? emptyChannel('facebook_messenger')}
          secrets={secrets.facebook_messenger}
          onChange={(row) => setForm((f) => ({ ...f!, facebook_messenger: row }))}
          onSecrets={(s) =>
            setSecrets((prev) => ({
              ...prev,
              facebook_messenger: { ...prev.facebook_messenger, ...s },
            }))
          }
        />
        {form.gmail ? (
          <GmailCard
            row={form.gmail}
            password={gmailPassword}
            onPassword={setGmailPassword}
            onChange={(row) => setForm((f) => ({ ...f!, gmail: row }))}
          />
        ) : null}
      </div>
    </>
  );
}

function GmailCard({
  row,
  password,
  onPassword,
  onChange,
}: {
  row: CrmGmailChannel;
  password: string;
  onPassword: (v: string) => void;
  onChange: (row: CrmGmailChannel) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2 text-sm">
          <Mail className="h-4 w-4" /> Gmail (customer inbox)
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Customer support mailbox: SMTP for replies, IMAP polling for inbound emails. Use a Gmail
          app password. Separate from staff notification email in CRM Settings → Email.
        </p>
      </CardHeader>
      <CardBody className="grid gap-3 md:grid-cols-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={row.is_enabled}
            onChange={(e) => onChange({ ...row, is_enabled: e.target.checked })}
          />
          Enabled
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={row.inbound_enabled}
            onChange={(e) => onChange({ ...row, inbound_enabled: e.target.checked })}
          />
          Poll inbound (IMAP)
        </label>
        <div>
          <Label>Username (Gmail address)</Label>
          <Input value={row.username} onChange={(e) => onChange({ ...row, username: e.target.value })} />
        </div>
        <div>
          <Label>App password {row.has_password ? '(set)' : ''}</Label>
          <Input
            type="password"
            value={password}
            placeholder={row.has_password ? '••••••••' : ''}
            onChange={(e) => onPassword(e.target.value)}
          />
        </div>
        <div>
          <Label>From name</Label>
          <Input value={row.from_name} onChange={(e) => onChange({ ...row, from_name: e.target.value })} />
        </div>
        <div>
          <Label>From email</Label>
          <Input value={row.from_email} onChange={(e) => onChange({ ...row, from_email: e.target.value })} />
        </div>
        <div>
          <Label>SMTP host</Label>
          <Input value={row.smtp_host} onChange={(e) => onChange({ ...row, smtp_host: e.target.value })} />
        </div>
        <div>
          <Label>SMTP port</Label>
          <Input
            value={String(row.smtp_port)}
            onChange={(e) => onChange({ ...row, smtp_port: Number(e.target.value) || 587 })}
          />
        </div>
        <div>
          <Label>IMAP host</Label>
          <Input value={row.imap_host} onChange={(e) => onChange({ ...row, imap_host: e.target.value })} />
        </div>
        <div>
          <Label>IMAP port</Label>
          <Input
            value={String(row.imap_port)}
            onChange={(e) => onChange({ ...row, imap_port: Number(e.target.value) || 993 })}
          />
        </div>
        {row.last_poll_at ? (
          <p className="md:col-span-2 text-xs text-muted-foreground">
            Last poll {new Date(row.last_poll_at).toLocaleString()} — {row.last_poll_status || '—'}
            {row.last_poll_error ? ` · ${row.last_poll_error}` : ''}
          </p>
        ) : null}
        {row.last_error ? (
          <p className="md:col-span-2 text-xs text-danger">{row.last_error}</p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function ChannelCard({
  title,
  icon: Icon,
  hint,
  row,
  secrets,
  onChange,
  onSecrets,
  showWa,
}: {
  title: string;
  icon: typeof MessageCircle;
  hint?: string;
  row: CrmChannelAccount;
  secrets: { secret: string; token: string };
  onChange: (row: CrmChannelAccount) => void;
  onSecrets: (s: Partial<{ secret: string; token: string }>) => void;
  showWa?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2 text-sm">
          <Icon className="h-4 w-4" /> {title}
        </CardTitle>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </CardHeader>
      <CardBody className="grid gap-3 md:grid-cols-2">
        <label className="flex items-center gap-2 text-sm md:col-span-2">
          <input
            type="checkbox"
            checked={row.is_enabled}
            onChange={(e) => onChange({ ...row, is_enabled: e.target.checked })}
          />
          Enabled
        </label>
        <div>
          <Label>Display name</Label>
          <Input value={row.display_name} onChange={(e) => onChange({ ...row, display_name: e.target.value })} />
        </div>
        <div>
          <Label>Verify token</Label>
          <Input
            value={row.meta_verify_token}
            onChange={(e) => onChange({ ...row, meta_verify_token: e.target.value })}
          />
        </div>
        <div>
          <Label>App ID</Label>
          <Input value={row.meta_app_id} onChange={(e) => onChange({ ...row, meta_app_id: e.target.value })} />
        </div>
        <div>
          <Label>Page ID</Label>
          <Input value={row.meta_page_id} onChange={(e) => onChange({ ...row, meta_page_id: e.target.value })} />
        </div>
        <div>
          <Label>IG user ID</Label>
          <Input
            value={row.meta_ig_user_id}
            onChange={(e) => onChange({ ...row, meta_ig_user_id: e.target.value })}
          />
        </div>
        {showWa ? (
          <>
            <div>
              <Label>Phone number ID (customer)</Label>
              <Input
                value={row.cloud_phone_number_id}
                onChange={(e) => onChange({ ...row, cloud_phone_number_id: e.target.value })}
              />
            </div>
            <div>
              <Label>WABA ID</Label>
              <Input
                value={row.cloud_waba_id}
                onChange={(e) => onChange({ ...row, cloud_waba_id: e.target.value })}
              />
            </div>
          </>
        ) : null}
        <div>
          <Label>App secret {row.has_meta_app_secret ? '(set)' : ''}</Label>
          <Input
            type="password"
            value={secrets.secret}
            placeholder={row.has_meta_app_secret ? '••••••••' : ''}
            onChange={(e) => onSecrets({ secret: e.target.value })}
          />
        </div>
        <div>
          <Label>Access token {row.has_meta_access_token ? '(set)' : ''}</Label>
          <Input
            type="password"
            value={secrets.token}
            placeholder={row.has_meta_access_token ? '••••••••' : ''}
            onChange={(e) => onSecrets({ token: e.target.value })}
          />
        </div>
        {row.last_error ? (
          <p className="md:col-span-2 text-xs text-danger">{row.last_error}</p>
        ) : null}
      </CardBody>
    </Card>
  );
}
